import React, { createContext, useContext, useState, useEffect } from 'react';
import { authService } from '../services/apiService';
import api from '../services/api';
import { getDefaultPermissions } from '../config/routeConfig';
import { subscribeToPush, unsubscribeFromPush, isPushSupported, getNotificationPermission } from '../services/pushService';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [screenPermissions, setScreenPermissions] = useState(null);
  const [sessionReplacedMsg, setSessionReplacedMsg] = useState('');
  const [deployUpdateMsg, setDeployUpdateMsg] = useState('');
  const [deployNotes, setDeployNotes] = useState([]);
  // ── What's New overlay (post-login, one-time per deployment) ────────────────
  const [showWhatsNew, setShowWhatsNew] = useState(false);
  const [whatsNewNotes, setWhatsNewNotes] = useState([]);
  const [whatsNewVersion, setWhatsNewVersion] = useState('');

  // Called when user taps "Ok, Thanks!" — marks this deployment's notes as seen
  const dismissWhatsNew = () => {
    if (whatsNewVersion) localStorage.setItem('notesSeenVersion', whatsNewVersion);
    setShowWhatsNew(false);
    setWhatsNewNotes([]);
    setWhatsNewVersion('');
  };

  // Helper: show What's New overlay if user hasn't seen these notes yet
  const _maybeShowWhatsNew = (serverVersion, notes) => {
    if (!serverVersion || !notes?.length) return;
    const notesSeenVersion = localStorage.getItem('notesSeenVersion');
    if (notesSeenVersion !== serverVersion) {
      setWhatsNewVersion(serverVersion);
      setWhatsNewNotes(notes);
      setShowWhatsNew(true);
    }
  };
  // ─────────────────────────────────────────────────────────────────────────

  const fetchPermissions = async () => {
    try {
      const res = await api.get('/screen-permissions');
      const defaults = getDefaultPermissions();
      if (res.data.exists && Object.keys(res.data.permissions).length > 0) {
        const saved = res.data.permissions;
        // UNION with defaults: roles listed in routeConfig.js defaults are
        // ALWAYS granted for that path — admin's saved AC config only ADDS
        // extra roles on top. Prevents newly-added default roles (e.g.
        // 'metal_inventory_assistant' added to /diamond-verification) from
        // being silently blocked by stale saved config from before that change.
        const merged = { ...defaults };
        Object.keys(saved).forEach(path => {
          merged[path] = [...new Set([...(defaults[path] || []), ...(saved[path] || [])])];
        });
        setScreenPermissions(merged);
      } else {
        setScreenPermissions(null);
      }
    } catch {
      setScreenPermissions(null);
    }
  };

  // ── Session-replaced listener ─────────────────────────────────────────────
  // Fired by api.js interceptor when the backend returns 401 "session_replaced"
  // (meaning a new login on another device has invalidated this token)
  useEffect(() => {
    const handleSessionReplaced = () => {
      setCurrentUser(null);
      setScreenPermissions(null);
      localStorage.removeItem('authToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('cachedUser');
      setSessionReplacedMsg('You have been logged out because your account was accessed on another device.');
    };
    window.addEventListener('auth:session_replaced', handleSessionReplaced);
    return () => window.removeEventListener('auth:session_replaced', handleSessionReplaced);
  }, []);

  // ── Visibility / resume check ─────────────────────────────────────────────
  // When the user brings the app back to the foreground (tab switch, iOS/Android
  // resume), re-check the deployment version. If a new deployment with force_logout
  // happened while the user had the app open, they get logged out immediately
  // instead of waiting for the next cold start.
  // Cooldown: at most once every 5 minutes to avoid hammering the endpoint.
  useEffect(() => {
    const currentUserRef = { current: null };
    // keep a ref so the handler always sees the latest value without re-registering
    const _syncRef = () => { currentUserRef.current = null; };
    let lastCheck = 0;
    const COOLDOWN_MS = 5 * 60 * 1000;

    const handleResume = async () => {
      if (document.visibilityState !== 'visible') return;
      if (!currentUserRef.current) return;
      const now = Date.now();
      if (now - lastCheck < COOLDOWN_MS) return;
      lastCheck = now;

      try {
        const versionRes = await api.get('/version');
        const serverVersion = versionRes.data?.version;
        const notes = versionRes.data?.notes || [];
        const forceLogout = versionRes.data?.force_logout === true;
        const forceLogoutNonce = versionRes.data?.force_logout_nonce;
        const storedVersion = localStorage.getItem('deployVersion');
        const storedNonce = localStorage.getItem('forceLogoutNonce');

        // Logout if: force_logout=true AND (version changed OR nonce changed)
        const versionChanged = storedVersion && serverVersion && storedVersion !== serverVersion;
        const nonceChanged = forceLogoutNonce && storedNonce !== forceLogoutNonce;

        if (forceLogout && (versionChanged || nonceChanged)) {
          api.post('/auth/logout', { reason: 'deployment' }).catch(() => {});
          localStorage.removeItem('authToken');
          localStorage.removeItem('refreshToken');
          localStorage.removeItem('cachedUser');
          setCurrentUser(null);
          setScreenPermissions(null);
          setDeployNotes(notes);
          setDeployUpdateMsg('DiaForge has been updated! Please log in to continue.');
          localStorage.setItem('deployVersion', serverVersion);
          if (forceLogoutNonce) localStorage.setItem('forceLogoutNonce', forceLogoutNonce);
        }
        if (serverVersion) localStorage.setItem('deployVersion', serverVersion);
        if (forceLogoutNonce) localStorage.setItem('forceLogoutNonce', forceLogoutNonce);
      } catch { /* best-effort */ }
    };

    document.addEventListener('visibilitychange', handleResume);
    window.addEventListener('pageshow', handleResume);

    // expose a way for the currentUser state to update the ref without re-registering
    window._diaforge_updateUserRef = (u) => { currentUserRef.current = u; };

    return () => {
      document.removeEventListener('visibilitychange', handleResume);
      window.removeEventListener('pageshow', handleResume);
      delete window._diaforge_updateUserRef;
    };
  }, []); // register once — uses ref for currentUser

  // Keep the ref in sync whenever currentUser changes
  useEffect(() => {
    if (window._diaforge_updateUserRef) window._diaforge_updateUserRef(currentUser);
  }, [currentUser]);
  // ─────────────────────────────────────────────────────────────────────────

  useEffect(() => {
    const initAuth = async () => {
      const token = localStorage.getItem('authToken');
      if (token) {
        try {
          const user = await authService.getMe();
          setCurrentUser(user);
          // Cache user profile so we can restore it on slow-network / PWA cold-start
          localStorage.setItem('cachedUser', JSON.stringify(user));
          await fetchPermissions();

          // Re-save push subscription on every cold start (like WhatsApp).
          // If the backend lost the record (deploy, DB restart, browser rotated endpoint)
          // this silently restores it so background notifications keep working.
          if (isPushSupported() && getNotificationPermission() === 'granted') {
            subscribeToPush().catch(() => {});
          }

          // ── Deployment version check ──────────────────────────────────────
          try {
            const versionRes = await api.get('/version');
            const serverVersion = versionRes.data?.version;
            const deployNotes = versionRes.data?.notes || [];
            const forceLogout = versionRes.data?.force_logout === true;
            const forceLogoutNonce = versionRes.data?.force_logout_nonce;
            const storedVersion = localStorage.getItem('deployVersion');
            const storedNonce = localStorage.getItem('forceLogoutNonce');

            // Force logout when: force_logout=true AND (version changed OR nonce changed)
            const versionChanged = storedVersion && serverVersion && storedVersion !== serverVersion;
            const nonceChanged = forceLogoutNonce && storedNonce !== forceLogoutNonce;

            if (forceLogout && (versionChanged || nonceChanged)) {
              api.post('/auth/logout', { reason: 'deployment' }).catch(() => {});
              localStorage.removeItem('authToken');
              localStorage.removeItem('refreshToken');
              localStorage.removeItem('cachedUser');
              setCurrentUser(null);
              setScreenPermissions(null);
              setDeployNotes(deployNotes);
              setDeployUpdateMsg('DiaForge has been updated! Please log in to continue.');
              localStorage.setItem('deployVersion', serverVersion);
              if (forceLogoutNonce) localStorage.setItem('forceLogoutNonce', forceLogoutNonce);
              setLoading(false);
              return;
            }
            // Always store the latest version and nonce (silent — no logout)
            if (serverVersion) localStorage.setItem('deployVersion', serverVersion);
            if (forceLogoutNonce) localStorage.setItem('forceLogoutNonce', forceLogoutNonce);
            // Show one-time "What's New" overlay for the user now logged in
            _maybeShowWhatsNew(serverVersion, deployNotes);
          } catch {
            // Version check is best-effort — never block login on failure
          }
          // ─────────────────────────────────────────────────────────────────
        } catch (error) {
          const status = error?.response?.status;
          if (status === 401 || status === 403) {
            // Genuine auth failure (bad/expired token that couldn't be refreshed)
            // — clear everything and go to login
            api.post('/auth/logout', { reason: 'session_expired' }).catch(() => {});
            localStorage.removeItem('authToken');
            localStorage.removeItem('refreshToken');
            localStorage.removeItem('cachedUser');
          } else {
            // Network error, server error, Samsung battery-save kill, etc.
            // — DO NOT log out. Restore from the last-known cached profile so the
            //   user lands on their home screen, not the login page.
            const cached = localStorage.getItem('cachedUser');
            if (cached) {
              try {
                setCurrentUser(JSON.parse(cached));
                // Permissions might also be offline — fetchPermissions handles its own error
                await fetchPermissions();
              } catch {}
            }
            // If no cache exists at all, fall through to login (first-ever launch)
          }
        }
      }
      setLoading(false);
    };
    initAuth();
  }, []);

  const canAccess = (path) => {
    if (!currentUser) return false;
    const role = currentUser.role;
    if (role === 'admin') return true;
    if (screenPermissions) {
      const allowedRoles = screenPermissions[path];
      if (allowedRoles) return allowedRoles.includes(role);
    }
    const defaults = getDefaultPermissions();
    const defaultRoles = defaults[path];
    if (defaultRoles) return defaultRoles.includes(role);
    return false;
  };

  const login = async (email, password) => {
    try {
      const response = await authService.login(email, password);
      if (response.success) {
        localStorage.setItem('authToken', response.token);
        if (response.refresh_token) {
          localStorage.setItem('refreshToken', response.refresh_token);
        }
        setCurrentUser(response.user);
        // Cache user immediately so offline restarts work right after first login
        localStorage.setItem('cachedUser', JSON.stringify(response.user));
        setSessionReplacedMsg('');
        setDeployUpdateMsg('');
        setDeployNotes([]);        await fetchPermissions();

        // Auto-register push on this device if permission already granted
        if (isPushSupported() && getNotificationPermission() === 'granted') {
          subscribeToPush().catch(() => {});
        }

        // Show one-time "What's New" overlay for this deployment
        try {
          const versionRes = await api.get('/version');
          const serverVersion = versionRes.data?.version;
          const forceLogoutNonce = versionRes.data?.force_logout_nonce;
          const notes = versionRes.data?.notes || [];
          if (serverVersion) localStorage.setItem('deployVersion', serverVersion);
          if (forceLogoutNonce) localStorage.setItem('forceLogoutNonce', forceLogoutNonce);
          _maybeShowWhatsNew(serverVersion, notes);
        } catch { /* best-effort */ }

        return { success: true, user: response.user };
      }
      return { success: false, error: 'Login failed' };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Invalid credentials' };
    }
  };

  const signup = async (username, email, password) => {
    try {
      const response = await authService.signup(username, email, password);
      if (response.success) {
        localStorage.setItem('authToken', response.token);
        if (response.refresh_token) {
          localStorage.setItem('refreshToken', response.refresh_token);
        }
        setCurrentUser(response.user);
        localStorage.setItem('cachedUser', JSON.stringify(response.user));
        return { success: true, user: response.user };
      }
      return { success: false, error: 'Signup failed' };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Signup failed' };
    }
  };

  const logout = async (reason = 'manual') => {
    try {
      // Get current device push endpoint before unsubscribing
      let endpoint = null;
      if (isPushSupported()) {
        try {
          const reg = await navigator.serviceWorker.ready;
          const sub = await reg.pushManager.getSubscription();
          if (sub) endpoint = sub.endpoint;
        } catch {}
      }

      // Notify backend: deletes push subscription + invalidates token + logs reason
      await api.post('/auth/logout', { ...(endpoint ? { endpoint } : {}), reason }).catch(() => {});

      // Unsubscribe at browser level
      await unsubscribeFromPush().catch(() => {});
    } catch {}

    setCurrentUser(null);
    setScreenPermissions(null);
    localStorage.removeItem('authToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('cachedUser');
  };

  const value = {
    currentUser,
    login,
    signup,
    logout,
    loading,
    canAccess,
    screenPermissions,
    refreshPermissions: fetchPermissions,
    isAdvisor: currentUser?.role === 'advisor',
    sessionReplacedMsg,
    deployUpdateMsg,
    deployNotes,
    dismissDeployNotes: () => { setDeployNotes([]); setDeployUpdateMsg(''); },
    // What's New overlay
    showWhatsNew,
    whatsNewNotes,
    dismissWhatsNew,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
